/**
 * GuestCalendarView — /dashboard/content-review, the "Content review" page a
 * Guest (a brand's client) opens from the calendar header.
 *
 * It is the client's review of a month — the board and panels of
 * ClientReviewBoard — fed through the guest routes. Every client-visible month
 * of a brand the guest holds is theirs to answer; the calendar's "Content
 * review" button opens it with `?planId=`. The team has its own preview of this view inside the
 * calendar, so the page is guest-only.
 */
import { useMemo, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router";
import { ArrowLeft, Eye, MessageSquareReply } from "lucide-react";
import { usePermissions } from "@/hooks/usePermissions";
import { cn } from "@/utils/helpers";
import { useWorkspaceStore } from "@/stores/workspaceStore";
import ClientReviewBoard, {
  type ClientReviewBoardState,
} from "@/app/routes/client-portal/ClientReviewBoard";
import MonthLoadError from "@/app/routes/client-portal/review/MonthLoadError";
import { monthLabel } from "../calendar.utils";
import { guestPortalSource, useGuestMonths, type GuestMonth } from "./useGuestCalendar";

function BoardSkeleton() {
  return (
    <div className="flex w-full flex-col gap-8" aria-hidden="true">
      <div className="flex flex-col gap-3 border-b border-co-line2 pb-5">
        <div className="h-3 w-40 animate-pulse rounded-tag bg-co-hover" />
        <div className="h-8 w-72 animate-pulse rounded-tag bg-co-hover" />
        <div className="h-3 w-[min(100%,34rem)] animate-pulse rounded-tag bg-co-hover" />
      </div>
      <div className="flex flex-col">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="grid grid-cols-[5rem_minmax(0,1fr)] gap-4 rounded-card border border-co-line2 bg-co-surface px-4 py-5 mt-2">
            <div className="h-10 w-12 animate-pulse rounded-card bg-co-hover" />
            <div className="flex flex-col gap-2">
              <div className="h-3 w-24 animate-pulse rounded-tag bg-co-hover" />
              <div className="h-4 w-2/3 animate-pulse rounded-tag bg-co-hover" />
              <div className="h-3 w-1/2 animate-pulse rounded-tag bg-co-hover" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function MonthChips({
  months,
  selected,
  onSelect,
}: {
  months: GuestMonth[];
  selected: string;
  onSelect: (planId: string) => void;
}) {
  return (
    <nav aria-label="Months" className="flex flex-wrap gap-2">
      {months.map((m) => {
        const active = m.planId === selected;
        return (
          <button
            key={m.planId}
            type="button"
            onClick={() => onSelect(m.planId)}
            aria-current={active ? "page" : undefined}
            className={cn(
              "inline-flex h-8 items-center gap-2 rounded-pill px-3.5 text-[12.5px] transition-colors",
              active
                ? "bg-co-accent-dim font-semibold text-co-text"
                : "co-glass-control text-co-muted hover:text-co-text",
            )}
          >
            <span className="font-semibold">{monthLabel({ year: m.year, month: m.month })}</span>
          </button>
        );
      })}
    </nav>
  );
}

function Notice({ icon, children }: { icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-3 rounded-card bg-co-surface px-4 py-3">
      <span className="mt-0.5 shrink-0 text-co-faint">{icon}</span>
      <p className="tt-read-m leading-relaxed text-co-muted">{children}</p>
    </div>
  );
}

function boardState(state: ClientReviewBoardState) {
  if (state.kind === "loading") return <BoardSkeleton />;
  if (state.kind === "context-error") {
    return (
      <Notice icon={<Eye className="size-4" />}>
        This month isn&apos;t available to you any more. Pick another month, or ask your contact at the agency.
      </Notice>
    );
  }
  return <MonthLoadError onRetry={state.retry} minHeight="360px" />;
}

function BackToCalendar() {
  return (
    <Link
      to="/dashboard/content-calendar"
      className="unstyled-link inline-flex w-fit items-center gap-1.5 text-[12.5px] text-co-muted hover:text-co-text"
    >
      <ArrowLeft className="size-3.5" aria-hidden="true" />
      Back to the calendar
    </Link>
  );
}

export default function GuestCalendarView() {
  const { isGuest } = usePermissions();
  const brandId = useWorkspaceStore((s) => s.selectedBrandId);
  const { data: months, isLoading, isError } = useGuestMonths(isGuest ? brandId : null);
  // The calendar's "Content review" button opens the month it was on.
  const [searchParams] = useSearchParams();
  const [picked, setPicked] = useState<string | null>(() => searchParams.get("planId"));

  // Default to the newest month.
  const selected = useMemo(() => {
    if (!months?.length) return null;
    return months.find((m) => m.planId === picked) ?? months[0];
  }, [months, picked]);

  const source = useMemo(
    () => (brandId && selected ? guestPortalSource(brandId, selected.planId) : null),
    [brandId, selected],
  );

  if (!isGuest) return <Navigate to="/dashboard/content-calendar" replace />;

  if (!brandId) {
    return (
      <div className="mx-auto w-full max-w-[92rem]">
        <Notice icon={<Eye className="size-4" />}>Pick a brand to see its content calendar.</Notice>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="mx-auto w-full max-w-[92rem]">
        <BoardSkeleton />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="mx-auto w-full max-w-[92rem]">
        <Notice icon={<Eye className="size-4" />}>Could not load the calendar. It retries on its own.</Notice>
      </div>
    );
  }

  if (!months?.length || !selected || !source) {
    return (
      <div className="mx-auto flex w-full max-w-[92rem] flex-col gap-3 py-10">
        <BackToCalendar />
        <h1 className="tt-title-l font-extrabold uppercase text-co-text">Content review</h1>
        <p className="tt-read-m max-w-[60ch] leading-relaxed text-co-muted">
          No month is ready for you yet. When the team shares a month with you, its content ideas and
          creative briefs show up here for your review.
        </p>
      </div>
    );
  }

  const toolbar = (
    <div className="flex flex-col gap-3">
      <BackToCalendar />
      <MonthChips months={months} selected={selected.planId} onSelect={setPicked} />
      <Notice icon={<MessageSquareReply className="size-4" />}>
        Open a content idea to approve it, send it back with a note, or comment.
      </Notice>
    </div>
  );

  return (
    <ClientReviewBoard
      key={selected.planId}
      source={source}
      embedded
      toolbar={toolbar}
      renderState={(state) => (
        <div className="mx-auto flex w-full max-w-[92rem] flex-col gap-6">
          {toolbar}
          {boardState(state)}
        </div>
      )}
    />
  );
}
